export const PAYMENT_GATEWAY_PROVIDER = {
  STRIPE: 'STRIPE',
  PAYPAL: 'PAYPAL',
  AMAZON: 'AMAZON',
  BRAINTREE: 'BRAINTREE',
} as const

export interface AuthorizePaymentRequestDTO {
  paymentToken?: string
  gatewayParams?: {
    deviceData: string
  }
}

export type PaymentGatewayProvider = (typeof PAYMENT_GATEWAY_PROVIDER)[keyof typeof PAYMENT_GATEWAY_PROVIDER]

export const EXTERNAL_PAYMENT_SOURCE = {
  PAYPAL: 'PAYPAL',
} as const

export type ExternalPaymentSource = (typeof EXTERNAL_PAYMENT_SOURCE)[keyof typeof EXTERNAL_PAYMENT_SOURCE]

export const EXPRESS_PAYMENT_SOURCE = {
  PAYPAL_EXPRESS: 'PAYPAL_EXPRESS',
  AMAZON_PAY_EXPRESS: 'AMAZON_PAY_EXPRESS',
  APPLE_PAY_EXPRESS: 'APPLE_PAY_EXPRESS',
  GOOGLE_PAY_EXPRESS: 'GOOGLE_PAY_EXPRESS',
  BRAINTREE_GOOGLE_PAY_EXPRESS: 'BRAINTREE_GOOGLE_PAY_EXPRESS',
  BRAINTREE_APPLE_PAY_EXPRESS: 'BRAINTREE_APPLE_PAY_EXPRESS',
} as const

export type ExpressPaymentSource = (typeof EXPRESS_PAYMENT_SOURCE)[keyof typeof EXPRESS_PAYMENT_SOURCE]

export const PAYMENT_SOURCE = {
  ...EXTERNAL_PAYMENT_SOURCE,
  ...EXPRESS_PAYMENT_SOURCE,
  CARD: 'CARD',
  AMAZON_PAY: 'AMAZON_PAY',
  GOOGLE_PAY: 'GOOGLE_PAY',
  APPLE_PAY: 'APPLE_PAY',
  SAVED_CARD: 'SAVED_CARD',
  KLARNA: 'KLARNA',
  AFTER_PAY: 'AFTER_PAY',
  BRAINTREE_GOOGLE_PAY_EXPRESS: 'BRAINTREE_GOOGLE_PAY_EXPRESS',
  BRAINTREE_APPLE_PAY_EXPRESS: 'BRAINTREE_APPLE_PAY_EXPRESS',
} as const

export type PaymentSource = (typeof PAYMENT_SOURCE)[keyof typeof PAYMENT_SOURCE]

export type MerchantOfRecordWiseAmountDistribution = Record<string, number>

export interface UnifiedGatewaySession {
  id: number
  paymentGatewayProvider: PaymentGatewayProvider
  paymentSourceDetails?: PaymentMethodSource[]
  merchantOfRecordWiseAmountDistribution?: MerchantOfRecordWiseAmountDistribution
}

export type PaymentMethodSource = PaypalPaymentMethodDetail | StripePaymentMethodDetail | BraintreePaymentMethodDetail

export interface BasePaymentMethodDetail {
  id: number
  sessionId: number
  paymentSource: PaymentSource
  status: 'PENDING' | 'CREATED'
  paymentGatewayProvider: PaymentGatewayProvider
}

export interface StripePaymentMethodDetail extends BasePaymentMethodDetail {
  paymentGatewayProvider: 'STRIPE'
  paymentSource: PaymentSource
  paymentProviderMetaData: StripePaymentProviderMetaData
  paymentMethodMetaData: null
  paymentSourceMetaData?: {
    isCVCRequired?: boolean
  }
}

interface StripePaymentProviderMetaData {
  clientSecret: string
  publishableKey: string
}

export interface PaypalPaymentMethodDetail extends BasePaymentMethodDetail {
  paymentGatewayProvider: 'PAYPAL'
  paymentSource: 'PAYPAL'
  paymentProviderMetaData: PaypalPaymentMethodMetadata
  paymentMethodMetaData: null
}

interface PaypalPaymentMethodMetadata {
  orderId: string
}

export interface BraintreePaymentMethodDetail extends BasePaymentMethodDetail {
  paymentGatewayProvider: 'BRAINTREE'
  paymentSource: PaymentSource
  paymentProviderMetaData: BraintreePaymentProviderMetaData
  paymentMethodMetaData: null
  paymentSourceMetaData?: {
    isCVCRequired?: boolean
  }
}

interface BraintreePaymentProviderMetaData {
  clientToken: string
  environment?: 'sandbox' | 'production'
  merchantId?: string
}

export const CARD_TYPE = {
  CREDIT: 'CREDIT',
  DEBIT: 'DEBIT',
  PREPAID: 'PREPAID',
  CHARGE: 'CHARGE',
  DEFERRED_DEBIT: 'DEFERRED_DEBIT',
  UN_HANDLED: 'UN_HANDLED',
} as const

interface CardPaymentInput {
  cardToken: string
}

interface SavedCardPaymentInput {
  cardId: number
  cvcToken?: string
}

export type PaymentSourceInput = {
  paymentSource: PaymentSource
} & (
  | {
      paymentSource: 'CARD'
      cardPaymentInput: CardPaymentInput
    }
  | {
      paymentSource: 'SAVED_CARD'
      savedCardPaymentInput?: SavedCardPaymentInput
    }
  | {
      paymentSource: 'PAYPAL'
      payPalPaymentInput: {
        returnUrl: string
        cancelUrl: string
      }
    }
  | {
      paymentSource: 'AMAZON_PAY'
      savedCardPaymentInput?: null
    }
  | {
      paymentSource: 'GOOGLE_PAY'
      savedCardPaymentInput?: null
    }
  | {
      paymentSource: 'APPLE_PAY'
      savedCardPaymentInput?: null
    }
  | {
      paymentSource: 'KLARNA'
    }
  | {
      paymentSource: 'AFTER_PAY'
    }
  | {
      paymentSource: 'PAYPAL_EXPRESS'
    }
  | {
      paymentSource: 'APPLE_PAY_EXPRESS'
    }
  | {
      paymentSource: 'GOOGLE_PAY_EXPRESS'
    }
  | {
      paymentSource: 'AMAZON_PAY_EXPRESS'
      amazonPayExpressPaymentInput?: {
        thirdPartyIntentId: string
      }
    }
  | {
      paymentSource: 'BRAINTREE_GOOGLE_PAY_EXPRESS'
    }
  | {
      paymentSource: 'BRAINTREE_APPLE_PAY_EXPRESS'
    }
)

export type UpdatePaymentSource = (paymentSourceInput: PaymentSourceInput) => Promise<UnifiedGatewaySession>
